import { FunctionComponent, useCallback, useEffect } from 'react'

import useTracking from '@hooks/useTracking'
import { getKropkaConfigObject } from '@utils/kropka'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import useGDPRApplies from '@hooks/useGDPRApplies'

interface CMPTrackingData {
  extraData: {
    consent: boolean
  }
}

const dataProtectionSitePath = '/services/datenschutz/opt-out/'

const Kropka: FunctionComponent = () => {
  const gdprApplies = useGDPRApplies()

  const trackingOnOverlayShown = useCallback(() => {
    return {
      event: 'consent_overlay_show',
      eventCategory: 'consent',
      eventAction: 'show',
      hasConsent: undefined,
    }
  }, [])

  const trackingOnConfigurationSaved = useCallback(
    ({ extraData: { consent } }: CMPTrackingData) => {
      return {
        event: 'consent_configuration_saved',
        eventCategory: 'consent',
        eventAction: 'saved',
        hasConsent: consent ? '1' : '0',
      }
    },
    []
  )

  const trackingOnOverlayAcceptAll = useCallback(
    ({ extraData: { consent } }: CMPTrackingData) => {
      return {
        event: 'consent_overlay_to_accept_all',
        eventCategory: 'consent',
        eventAction: 'accept_all',
        hasConsent: consent ? '1' : '0',
      }
    },
    []
  )

  const trackOverlayShown = useTracking(trackingOnOverlayShown)
  const trackConfigurationSaved = useTracking(trackingOnConfigurationSaved)
  const trackOverlayAcceptAll = useTracking(trackingOnOverlayAcceptAll)

  const onTcfApiEvent = useCallback(
    (event: any) => {
      // Do not track the showing of the CMP overlay, when opened
      // on the data protection opt-out page
      if (
        event.eventStatus === 'cmpuishown' &&
        window.location.pathname !== dataProtectionSitePath
      ) {
        trackOverlayShown()
      }
    },
    [trackOverlayShown]
  )

  const onConsentChange = useCallback(() => {
    window.dlApi.hasVendorsConsent?.((consent: boolean) => {
      window.location.pathname === dataProtectionSitePath
        ? trackConfigurationSaved({ consent })
        : trackOverlayAcceptAll({ consent })
    })
  }, [trackConfigurationSaved, trackOverlayAcceptAll])

  useEffect(() => {
    window.dlApi.cmd.push(() => {
      // Used for detecting when the CMP overlay is shown to the user
      window.__tcfapi('addEventListener', 2, onTcfApiEvent)
      // Used for detecting when a user changed a consent inside the CMP overlay
      window.dlApi.on?.('consentsChange', onConsentChange)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sendKropkaPageView = useCallback(
    (pageMetadata: any) => {
      // Tells Kropka, that it should track a page view
      window.dlApi.changeView?.(
        getKropkaConfigObject({
          metadata: pageMetadata,
          gdprApplies,
        })
      )
    },
    [gdprApplies]
  )

  useExecuteOnClientNavigation(sendKropkaPageView)

  return null
}

export default Kropka
